import { Avatar, Box, IconButton, Typography } from "@mui/material";
import { colors } from "../../../theme/palette";
import { useTranslation } from "react-i18next";
import { UserInfo } from "../../../types";
import { useDataFormatter } from "../../../hooks/useDataFormatter";
import {
  AcceptPlayerIcon,
  DeletePlayerIcon,
} from "../../../assets/icons/MUI/EventsIcons";
import {
  QueryObserverResult,
  RefetchOptions,
  useQueryClient,
} from "@tanstack/react-query";
import {
  acceptRequestingPlayersFromEvent,
  removeRequestingPlayersFromEvent,
} from "../../../api/events";

const RequestsList = ({
  isOwnEvent,
  requestsList,
  eventId,
  refetch,
}: {
  isOwnEvent: boolean;
  requestsList: UserInfo[];
  eventId?: string;
  refetch: () => void;
}) => {
  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {requestsList.length > 0 &&
        requestsList.map((player: any, index: number) => {
          const isLast = requestsList.length === index + 1;
          return (
            <PlayerCard
              key={player.id}
              player={player}
              isLast={isLast}
              isOwnEvent={isOwnEvent}
              eventId={eventId}
              refetch={refetch}
            />
          );
        })}
    </Box>
  );
};

export default RequestsList;

const PlayerCard = ({
  player,
  isLast,
  isOwnEvent,
  eventId,
  refetch,
}: {
  player: any;
  isLast: boolean;
  isOwnEvent: boolean;
  eventId?: string;
  refetch: () => void;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { id, fullName, firstTwoChr, media, role, club, mutualFollowersCount } =
    useDataFormatter(player.requestedUser);

  return (
    <Box
      sx={{
        display: "flex",
        maxHeight: "100%",
        overflow: "auto",
        columnGap: "20px",
        paddingBottom: "16px",
        marginBottom: isLast ? "" : "16px",
        borderBottom: isLast ? "" : "1px solid #E7E7E7",
      }}
    >
      <Avatar
        src={media}
        sx={{
          width: "47px",
          height: "47px",
        }}
      >
        {firstTwoChr}
      </Avatar>
      <Box
        sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}
      >
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="body16SemiBold" sx={{ color: "#171A1C" }}>
            {fullName}
          </Typography>
          <Typography variant="body14Regular" sx={{ color: colors.Dark1000 }}>
            {role}
            {club && ` . ${club}`}
          </Typography>
          <Typography variant="body14Regular" sx={{ color: colors.Dark600 }}>
            {mutualFollowersCount}{" "}
            {t("pages.events.eventDetails.mutualFollowers")}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: "8px" }}>
          {isOwnEvent && (
            <IconButton
              sx={{ width: "fit-content", height: "fit-content" }}
              onClick={async () => {
                try {
                  await acceptRequestingPlayersFromEvent(
                    eventId,
                    `${player.requestId}`
                  );
                  queryClient.invalidateQueries({
                    queryKey: ["eventJoinRequests"],
                  });
                  refetch();
                } catch (error) {}
              }}
            >
              <AcceptPlayerIcon />
            </IconButton>
          )}
          {isOwnEvent && (
            <IconButton
              sx={{ width: "fit-content", height: "fit-content" }}
              onClick={async () => {
                try {
                  await removeRequestingPlayersFromEvent(
                    eventId,
                    `${player.requestId}`
                  );
                  queryClient.invalidateQueries({
                    queryKey: ["eventJoinRequests"],
                  });
                  refetch();
                } catch (error) {}
              }}
            >
              <DeletePlayerIcon />
            </IconButton>
          )}
        </Box>
      </Box>
    </Box>
  );
};
